import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { PackageCallout } from "@/components/PackageCallout";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";

import heroImage from "@/assets/outdoor-kitchen-installation-totowa-nj.webp";
import foundationsImage from "@/assets/outdoor-kitchen-installation-totowa-nj-2.webp";
import layoutImage from "@/assets/outdoor-kitchen-installation-pompton-lakes-nj.webp";
import contractorImage from "@/assets/outdoor-kitchen-montclair-nj.webp";
import roiImage from "@/assets/outdoor-kitchen-richfield-clifton-nj.webp";

const CitationLink = ({ num, url, children }: { num: number; url: string; children?: React.ReactNode }) => (
  <a href={url} target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">
    {children}<sup>[{num}]</sup>
  </a>
);

const faqs = [
  {
    question: "Do I need a permit to install an outdoor kitchen in North Jersey?",
    answer: "Yes, most North Jersey municipalities require permits for outdoor kitchens, especially when gas lines, electrical, or plumbing are involved. Towns like Wayne, Montclair, and Glen Rock each have specific zoning and setback requirements. We handle the permit process as part of our service.",
  },
  {
    question: "How far should an outdoor kitchen be from my house in North Jersey?",
    answer: "Most NJ towns require outdoor kitchens to be at least 10 feet from the main structure, though this varies by municipality. Fire codes and property line setbacks also apply. We verify your local requirements before design begins.",
  },
  {
    question: "Do outdoor kitchens in North Jersey need footings?",
    answer: "Yes. New Jersey's 36-inch frost line means outdoor kitchen structures need proper footings to prevent shifting and cracking. We pour reinforced concrete footings below the frost line for every masonry kitchen we build.",
  },
  {
    question: "Should I hire a contractor or DIY my outdoor kitchen?",
    answer: "DIY outdoor kitchens often fail within a few years due to skipped footings, improper drainage, or materials that can't handle freeze-thaw cycles. Hiring a licensed mason ensures your kitchen is built to code, properly permitted, and designed to last decades.",
  },
  {
    question: "What size outdoor kitchen works for a typical North Jersey backyard?",
    answer: "Most North Jersey backyards accommodate an 8-12 foot island or L-shaped layout. NKBA guidelines recommend 42 inches of aisle space for one cook, 48 inches for two. We design layouts that maximize function without overwhelming your outdoor space.",
  },
];

const citations = [
  {
    num: 1,
    title: "NAR 2023 Remodeling Impact Report",
    url: "https://www.nar.realtor/research-and-statistics/research-reports/remodeling-impact-report-outdoor-features",
  },
  {
    num: 2,
    title: "NJ DCA Construction Code Communicator, Fall 2023",
    url: "https://www.nj.gov/dca/codes/",
  },
  {
    num: 3,
    title: "NKBA Kitchen & Bath Planning Guidelines",
    url: "https://nkba.org/",
  },
  {
    num: 4,
    title: "CMHA TEK 05-03A: Reinforced Masonry",
    url: "https://masonryandhardscapes.org/",
  },
  {
    num: 5,
    title: "ICC Digital Codes: NJ Residential Code 2021",
    url: "https://codes.iccsafe.org/",
  },
  {
    num: 6,
    title: "Outdoor Kitchen Ideas - Architectural Digest",
    url: "https://www.architecturaldigest.com/gallery/outdoor-kitchen-ideas",
  },
  {
    num: 7,
    title: "Read This Before You Put in an Outdoor Kitchen - This Old House",
    url: "https://www.thisoldhouse.com/yards/read-this-before-you-put-in-an-outdoor-kitchen",
  },
];

export default function OutdoorKitchens() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Outdoor Kitchen Installation Totowa, NJ | Masonry Pro</title>
        <meta property="og:description" content="Thinking about an outdoor kitchen for your North Jersey home? We handle the design, permits, and masonry work so you get a setup that lasts for years." />
        <meta property="og:title" content="Outdoor Kitchen Installation Totowa, NJ | Masonry Pro" />
        <meta 
          name="description" 
          content="Thinking about an outdoor kitchen for your North Jersey home? We handle the design, permits, and masonry work so you get a setup that lasts for years." 
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer,
              },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Outdoor Kitchen Installation",
            description: "Custom outdoor kitchen installation in North Jersey with proper permits, footings, and masonry work.",
            url: "/masonry-services/outdoor-kitchens",
            image: "https://masonry-pro.com/og/outdoor-kitchens.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/outdoor-kitchens.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/outdoor-kitchens.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        
        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Outdoor kitchen construction in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-3xl">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Outdoor Kitchen Installation in Totowa, NJ Serving North Jersey: <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Built to Last Every Season</span>
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.25rem', marginBottom: '2rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Custom masonry islands, stone veneer, and fire features designed to handle NJ weather and weekend hosting.
                </p>
                <Button variant="gold" size="lg" asChild>
                  <a href="tel:+19737252395" className="flex items-center gap-2">
                    <Phone className="h-5 w-5" />
                    Call or Text (973) 725-2395
                  </a>
                </Button>
              </div>
            </div>
          </section>

          <PackageCallout packageId="outdoor-kitchen" packageName="Outdoor Kitchen Package" blurb={"Bundle your outdoor kitchen with a properly built paver patio base, stone-veneered island, and utility runs. We design and build it as one project, not bolted together later."} />

          {/* Intro Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto space-y-6 text-lg text-muted-foreground">
                <p>
                  An outdoor kitchen extends your living area and adds real value to your property. But in North Jersey, where freeze-thaw cycles, heavy rain, and humidity are part of the deal, cutting corners leads to cracked countertops and crumbling masonry within a few years. We build outdoor kitchens the same way we build everything else as a <Link to="/" className="text-secondary hover:underline">masonry contractor based in Totowa</Link>: on a solid base.
                </p>
                <p>
                  We build outdoor kitchens with <CitationLink num={4} url="https://masonryandhardscapes.org/">reinforced masonry foundations</CitationLink>, proper footings below the frost line, and materials rated for our climate. No shortcuts that fall apart after the first winter. Watch an <Link to="/videos/outdoor-kitchen-fire-pit-north-caldwell-nj" className="text-secondary hover:underline">outdoor kitchen and fire pit we built in North Caldwell</Link>.
                </p>
              </div>
            </div>
          </section>

          {/* Masonry Foundations Section - Text Left, Image Right */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="space-y-6">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Masonry Foundations <span className="text-gradient-gold">Built for NJ Weather</span>
                  </h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>
                      Every outdoor kitchen we build starts with concrete masonry block (CMU) construction. Per <CitationLink num={2} url="https://www.nj.gov/dca/codes/">NJ DCA standards</CitationLink>, footings must extend 36 inches below grade to get below the frost line.
                    </p>
                    <p>
                      We follow <CitationLink num={4} url="https://masonryandhardscapes.org/">CMHA TEK 05-03A guidelines</CitationLink> for reinforced masonry, including vertical rebar and grouted cells at corners and beside openings. This prevents the shifting and cracking that plagues DIY and prefab outdoor kitchens.
                    </p>
                    <p>
                      The result: a structure that handles thousands of freeze-thaw cycles without moving.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img 
                    src={foundationsImage} 
                    alt="Outdoor kitchen installation featuring a brick pizza oven and built-in grill in Totowa, Passaic County, New Jersey"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Building Permits Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
                  Building Permits <span className="text-gradient-gold">& Code Compliance</span>
                </h2>
                <div className="space-y-6 text-muted-foreground">
                  <p>
                    Most outdoor kitchens in North Jersey require permits, especially when gas, plumbing, or electrical work is involved. Per <CitationLink num={5} url="https://codes.iccsafe.org/">NJ Residential Code</CitationLink>, any structure with utility connections triggers subcode inspections.
                  </p>
                  <p>
                    Towns in <Link to="/service-areas/morris-county" className="text-secondary hover:underline">Morris</Link>, <Link to="/service-areas/bergen-county" className="text-secondary hover:underline">Bergen</Link>, and <Link to="/service-areas/essex-county" className="text-secondary hover:underline">Essex</Link> counties each have their own zoning requirements. Morristown may require a 15-foot setback from property lines; Wayne might allow 10 feet. Some municipalities restrict outdoor kitchen placement near pools or property boundaries.
                  </p>
                  <p className="font-semibold text-foreground">
                    We handle the permit process as part of our service. We know the inspectors, the requirements, and how to get approvals without delays.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Layout and Sizing Section - Image Left, Text Right */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="aspect-[4/3] rounded-xl overflow-hidden order-2 lg:order-1">
                  <img 
                    src={layoutImage} 
                    alt="New outdoor kitchen installation with granite countertops and grill next to a pool in Dupont Village, Pompton Lakes, New Jersey"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="space-y-6 order-1 lg:order-2">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Layout & Sizing <span className="text-gradient-gold">Guidelines</span>
                  </h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>
                      <CitationLink num={3} url="https://nkba.org/">NKBA kitchen planning guidelines</CitationLink> recommend 42 inches of aisle space for one cook, 48 inches for two. Landing areas of at least 15 inches should flank each side of the cooking surface.
                    </p>
                    <p>
                      For typical North Jersey backyards, we often recommend L-shaped layouts for properties in Wyckoff and Glen Rock, U-shaped configurations for larger lots in Chatham and Madison. Most suburban lots accommodate an 8-12 foot island comfortably.
                    </p>
                    <p>
                      We design layouts that maximize function without overwhelming your outdoor space, leaving room for seating, landscaping, and foot traffic.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Why Hire a Contractor Section - Text Left, Image Right */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="space-y-6">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Why Hire a <span className="text-gradient-gold">Licensed Contractor?</span>
                  </h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>
                      DIY outdoor kitchens fail for predictable reasons: skipped footings, materials not rated for freeze-thaw, poor drainage, and unpermitted gas work. According to <CitationLink num={7} url="https://www.thisoldhouse.com/yards/read-this-before-you-put-in-an-outdoor-kitchen">This Old House</CitationLink>, these shortcuts lead to expensive repairs within 3-5 years.
                    </p>
                    <p>
                      Bergen and Passaic counties have clay-heavy soil that shifts seasonally. Without proper footings and drainage, even well-built structures can heave and crack. We understand local soil conditions and build accordingly.
                    </p>
                    <p>
                      A licensed mason ensures your kitchen is built to code, properly permitted, and designed to last decades, not just a few summers.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img 
                    src={contractorImage} 
                    alt="Custom outdoor kitchen with stone finish and stainless steel appliances waiting for concrete countertops Upper Montclair, NJ"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Value and ROI Section - Image Left, Text Right */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="aspect-[4/3] rounded-xl overflow-hidden order-2 lg:order-1">
                  <img 
                    src={roiImage} 
                    alt="Outdoor kitchen with a built-in grill and refrigerator in Richfield, Clifton, Passaic County, New Jersey"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="space-y-6 order-1 lg:order-2">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Value & <span className="text-gradient-gold">Return on Investment</span>
                  </h2>
                  <div className="space-y-4 text-muted-foreground">
                    <p>
                      The <CitationLink num={1} url="https://www.nar.realtor/research-and-statistics/research-reports/remodeling-impact-report-outdoor-features">NAR 2023 Remodeling Impact Report</CitationLink> shows outdoor kitchens recover 100% of their cost at resale, with a "Joy Score" of 9 out of 10, one of the highest-rated home improvements.
                    </p>
                    <p>
                      In Bergen and Essex counties, buyers expect move-in-ready outdoor spaces. Properties in Maplewood, Livingston, and Short Hills with quality outdoor kitchens sell faster and command premium prices.
                    </p>
                    <p>
                      Beyond resale value, you're adding usable living space for entertaining, family dinners, and weekend cooking. That's functionality that pays dividends every season.
                    </p>
                    <p>
                      An outdoor kitchen is one of the larger investments you can make in your backyard, and we make it easier with financing options through our partner Acorn Finance. Pre-qualification uses a soft credit check, so checking your offers will not affect your credit score. <Link to="/financing" className="text-secondary hover:underline">Explore financing for your outdoor kitchen project</Link>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Footings Requirements Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
                  Footing Requirements <span className="text-gradient-gold">for NJ Outdoor Kitchens</span>
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-center mb-8">
                    Every outdoor kitchen we build follows this process to ensure long-term stability:
                  </p>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[
                      { step: 1, title: "Check Local Requirements", desc: "Verify municipality's footing depth and zoning setbacks" },
                      { step: 2, title: "Determine Frost Line", desc: "36 inches below grade per NJ DCA standards" },
                      { step: 3, title: "Excavate Properly", desc: "Dig below frost line to undisturbed soil" },
                      { step: 4, title: "Pour Reinforced Footings", desc: "Concrete footings sized for masonry load with rebar" },
                      { step: 5, title: "Set First Course", desc: "Lay first course of block into footing, verify level" },
                    ].map((item) => (
                      <div key={item.step} className="bg-background rounded-xl p-6 border border-border">
                        <div className="w-10 h-10 rounded-full bg-gradient-navy flex items-center justify-center mb-4">
                          <span className="text-secondary font-bold">{item.step}</span>
                        </div>
                        <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* YouTube Shorts Section */}
          <YouTubeShortsSection 
            title="See Our Outdoor Kitchens"
            highlightedTitle="In Action"
            videoIds={["M16CRt8dmJ4", "0RG6pVGG92E", "7N5f77AabdU", "Fogsfyz7P18"]}
          />

          {/* FAQ Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked <span className="text-gradient-gold">Questions</span>
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq) => (
                  <div key={faq.question} className="bg-card rounded-xl p-6 border border-border">
                    <h3 className="text-lg font-bold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground text-sm">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <RelatedServices currentCategory="masonry" />

          {/* CTA Section */}
          <CTASection serviceName="outdoor kitchen" showFinancing />

          {/* Citations Accordion */}
          <section className="py-12 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Accordion type="single" collapsible>
                  <AccordionItem value="citations" className="border-border">
                    <AccordionTrigger className="text-muted-foreground hover:text-foreground">
                      Citations & References
                    </AccordionTrigger>
                    <AccordionContent>
                      <ol className="space-y-2 text-sm text-muted-foreground">
                        {citations.map((citation) => (
                          <li key={citation.num}>
                            [{citation.num}]{" "}
                            <a 
                              href={citation.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-secondary hover:underline"
                            >
                              {citation.title}
                            </a>
                          </li>
                        ))}
                      </ol>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
